import React, { useId } from 'react';
import { useImpressionRef } from '@noths/polaris-client-react-hooks';
import { visuallyHidden } from '@noths/polaris-client-styles';
import { Icon } from '@noths/polaris-design-system-core-components';

import type { MaybeSettled, ReviewsSummaryAiSummary } from 'src/redux/reviews/types';
import { roundAverageProductRating } from 'src/utils/roundAverageProductRating';
import * as copy from '../constants/copy';
import { getAiSummaryNote } from '../helpers/getAiSummaryNote';
import * as styles from './ReviewsSummary.styles';

export interface ReviewsSummaryProps {
  /**
   * `undefined` while unsettled, `null` once settled with nothing. Either way
   * the section renders nothing: without the summary there is no section, only
   * a rating the page already shows elsewhere.
   */
  aiSummary: MaybeSettled<ReviewsSummaryAiSummary>;
  /** Already gated on the minimum response count; `null` hides the row. */
  asAdvertisedPercentage: number | null;
  averageRating: number | null;
  isMostLoved: boolean;
  onAiSummaryVisible?: () => void;
  onMostLovedVisible?: () => void;
  onVisible?: () => void;
}

export const ReviewsSummary = ({
  aiSummary,
  asAdvertisedPercentage,
  averageRating,
  isMostLoved,
  onAiSummaryVisible,
  onMostLovedVisible,
  onVisible,
}: ReviewsSummaryProps) => {
  const headingId = useId();
  const rootRef = useImpressionRef<HTMLElement>(() => onVisible?.());
  const aiSummaryRef = useImpressionRef<HTMLParagraphElement>(() => onAiSummaryVisible?.());
  const mostLovedRef = useImpressionRef<HTMLParagraphElement>(() => onMostLovedVisible?.());

  if (!aiSummary) {
    return null;
  }

  const roundedRating = roundAverageProductRating(averageRating);
  const showRating = Boolean(roundedRating);

  return (
    <section aria-labelledby={headingId} css={styles.root} ref={rootRef}>
      <div css={styles.header}>
        <h2 css={styles.heading} id={headingId}>
          {copy.HEADING}
        </h2>
        {(showRating || isMostLoved) && (
          <div css={styles.ratingContainer}>
            {showRating && (
              <p css={styles.rating}>
                <Icon name="starFull" size="md" />
                <span aria-hidden="true">{`${roundedRating}/5`}</span>
                <span css={visuallyHidden}>{`${roundedRating} out of 5`}</span>
              </p>
            )}
            {/*
              The pill's word spaces live inside the template literals rather
              than in JSX whitespace: it is one flowing sentence, and JSX strips
              whitespace around newlines, so a reformat would otherwise run the
              words together. The gap after the icon is its own margin.
            */}
            {isMostLoved && (
              <p css={styles.mostLoved} ref={mostLovedRef}>
                {`${copy.MOST_LOVED_PREFIX} `}
                <Icon name="mostLoved" size="sm" />
                <span css={styles.mostLovedLabel}>{copy.MOST_LOVED_LABEL}</span>
                {` ${copy.MOST_LOVED_SUFFIX}`}
              </p>
            )}
          </div>
        )}
      </div>
      <div css={styles.details}>
        <p css={styles.summaryText} ref={aiSummaryRef}>
          {aiSummary.text}
        </p>
        <p css={styles.summaryNote}>{getAiSummaryNote(aiSummary.lookbackMonths)}</p>
      </div>
      {asAdvertisedPercentage !== null && (
        <>
          <hr css={styles.divider} />
          <p css={styles.asAdvertised}>
            <Icon name="completeOrSuccess" size="md" />
            <span>
              <b>{`${asAdvertisedPercentage}%`}</b> {copy.AS_ADVERTISED_SUFFIX}
            </span>
          </p>
        </>
      )}
    </section>
  );
};
